/**
 * Importaciones necesarias para el componente
 */
import { Component, ViewChild } from '@angular/core';
import {
  DownloadRequest,
  DownloadResponse,
  DownloadDocumentsRequest,
  DownloadEndorsementRequest,
  ProcessTypes,
  DownloadData,
} from '../../../interfaces/utilitarios.interface';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { CommonModule } from '@angular/common';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { UtilitariosService } from '../../../services/utilitarios.service';
import { AuthService } from '../../../services/auth.service';
import { TableConfig } from '../../../interfaces/table-config.interface';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { DownloadFilter } from '../../../interfaces/filters.interfaces';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';
import { MarbetesService } from '../../../services/marbetes.service';
import { CarnetAllResponse } from '../../../interfaces/marbetes.interface';
import { formatLocalDate, subtractMonths } from '../../../helpers/date-helper';
import { unformatPolicy } from '../../../helpers/policy-formatter';
import { concat, of } from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';
import { LoadingService } from '../../../error&timer/loading.service';

/**
 * Componente para gestionar las descargas de documentos
 * Permite buscar y descargar facturas, condiciones particulares y endosos
 */
@Component({
  selector: 'app-descargas',
  standalone: true,
  imports: [
    CommonModule,
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
  ],
  templateUrl: './descargas.component.html',
  styleUrl: './descargas.component.css',
})
export class DescargasComponent {
  insuranceData: DownloadResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  fecha1?: Date;
  fecha2?: Date;
  clientCode?: number;
  policy?: string;
  isFoundPrint: boolean = false;
  preventDownload: boolean = false;
  pageNumber: number = 1;
  pageSize: number = 5;
  totalRecordsFromServer: number = 0;
  totalPagesFromServer: number = 0;
  private lastFilters?: DownloadFilter;
/** Mensaje por defecto del modal cuando no hay documentos */
private readonly defaultNoDocumentsMessage = 'No existen documentos disponibles para el reclamo seleccionado.';
noDocumentsAviableMessage: string = this.defaultNoDocumentsMessage;
  /**
   * Tipos de documentos que se pueden descargar
   */
  processTypes:ProcessTypes[] = [
    { process: 3, processName: 'Facturas' },
    { process: 4, processName: 'Condiciones Particulares' },
    { process: 5, processName: 'Endoso cesión derechos' },
    { process: 6, processName: 'Marbetes' },
    { process: 7, processName: 'Descargar todos' },
  ];


  /**
   * Configuración de la tabla que muestra los documentos
   */
  tableConfig: TableConfig = {
    columns: [
      {
        key: 'policyNumber',
        label: 'Póliza',
        sortable: true,
        type: 'poliza',
      },
      {
        key: 'clientCode',
        label: 'Código',
        sortable: true,
      },
      {
        key: 'clientName',
        label: 'Asegurado',
        sortable: true,
      },
      {
        key: 'efectiveDate',
        label: 'Vigencia',
        sortable: true,
        type: 'date',
      },
      {
        key: 'workDate',
        label: 'Emisión',
        sortable: true,
        type: 'date',
      },
      {
        key: 'documentNumber',
        label: 'Documento',
        sortable: true,
      },
      {
        key: 'abrType',
        label: 'Tipo de Documento',
        sortable: true,
      },
      {
        key: 'downloadDocuments',
        label: 'Descargar',
        type: 'downloadDocuments',
      },
    ],
    showTotals: true,
  };

  /**
   * Configuración de la tabla para exportaciones (sin columna de descarga)
   */
  tableConfigExport: TableConfig = {
    ...this.tableConfig,
    columns: this.tableConfig.columns.filter((c) => c.label !== 'Descargar'),
  };

  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  title: string = '';
  defaultDesde: string = formatLocalDate(subtractMonths(new Date(), 3));
  defaultHasta: string = formatLocalDate(new Date());

  /**
   * Constructor del componente
   * @param utilitariosService Servicio para gestionar las descargas
   * @param authService Servicio de autenticación
   * @param gAnalytics Servicio de Google Analytics
   * @param marbetesService Servicio de marbetes
   * @param loadingService Servicio de loading
   */
  constructor(
    private utilitariosService: UtilitariosService,
    private authService: AuthService,
    private gAnalytics: GoogleAnalyticsService,
    private marbetesService: MarbetesService,
    private loadingService: LoadingService
  ) {
    this.title = 'Descargas';
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza la petición al servidor con los nuevos filtros
   * @param filters Filtros de búsqueda seleccionados
   */
  onFilterChange(filters: DownloadFilter): void {
    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }

    this.fecha1 = filters.dateFrom;
    this.fecha2 = filters.dateTo!;
    this.clientCode = filters.clientCode;
    this.policy = unformatPolicy(
      this.camposComponent?.policy ?? filters.Policy
    );
    this.lastFilters = { ...filters, Policy: this.policy };
    this.pageNumber = 1;

    const request: DownloadRequest = {
      ProducerNumber: this.authService.getProducerNumber() || 0,
      ClientCode: this.clientCode || 0,
      Policy: this.policy,
      dateFrom: this.fecha1 || new Date(),
      dateTo: this.fecha2 || new Date(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
    };

    this.utilitariosService.getProduction(request).subscribe({
      next: (response: HttpResponse<ApiResponse<DownloadData>>) => {
        if (response.status === 200 && response.body?.data?.productions) {
          this.insuranceData = response.body?.data?.productions.map((item:any) => ({
            ...item,
            downloadDocuments: 'print',
            processTypes: this.processTypes,
            process: '',
          }));
          this.totalRecordsFromServer =
            response.body?.data?.pagination?.totalRecords || 0;
          this.totalPagesFromServer =
            response.body?.data?.pagination?.totalPages || 0;
          this.mensajeNoData = '';
        }

        if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener la producción:', error);
        this.insuranceData = [];

        this.mensajeNoData =
          'No se han podido obtener los datos de la descarga';
      },
    });
  }

  /**
   * Solicita al API la página seleccionada en la tabla
   * @param page Número de página a consultar
   */
  onPageChange(page: number): void {
    this.pageNumber = page;
    const filters = this.lastFilters;

    const request: DownloadRequest = {
      ProducerNumber: this.authService.getProducerNumber() || 0,
      ClientCode: filters?.clientCode || this.clientCode || 0,
      Policy: unformatPolicy(filters?.Policy || this.policy),
      dateFrom: filters?.dateFrom || this.fecha1 || new Date(),
      dateTo: filters?.dateTo || this.fecha2 || new Date(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
    };

    this.utilitariosService.getProduction(request).subscribe({
      next: (response: HttpResponse<ApiResponse<DownloadData>>) => {
        if (response.status === 200 && response.body?.data?.productions) {
          this.insuranceData = response.body?.data?.productions.map((item:any) => ({
            ...item,
            downloadDocuments: 'print',
            processTypes: this.processTypes,
            process: '',
          }));
          this.totalRecordsFromServer =
            response.body?.data?.pagination?.totalRecords || 0;
          this.totalPagesFromServer =
            response.body?.data?.pagination?.totalPages || 0;
          this.mensajeNoData = '';
        }

        if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener la producción:', error);
        this.insuranceData = [];

        this.mensajeNoData =
          'No se han podido obtener los datos de la descarga';
      },
    });
  }

  /**
   * Exporta todos los registros del reporte según los filtros actuales
   * Consulta el total de descargas al API y los usa solo para la exportación
   * @param formato Formato de exportación ('xlsx', 'pdf', etc)
   */
  onExportar(formato: string): void {
    if (!this.tablaComponent || this.totalRecordsFromServer === 0) {
      return;
    }

    const headerInfo = {
      titulo: 'Descargas',
      lob: this.getLobName(),
      slob: this.getSlobName(),
      fecha1: this.fecha1!,
      fecha2: this.fecha2!,
      tableConfig: this.tableConfig,
    };

    const filters = this.lastFilters;
    const request: DownloadRequest = {
      ProducerNumber: this.authService.getProducerNumber() || 0,
      ClientCode: filters?.clientCode || this.clientCode || 0,
      Policy: unformatPolicy(filters?.Policy || this.policy),
      dateFrom: filters?.dateFrom || this.fecha1 || new Date(),
      dateTo: filters?.dateTo || this.fecha2 || new Date(),
      pageNumber: 1,
      pageSize: this.totalRecordsFromServer,
    };

    this.utilitariosService.getProduction(request).subscribe({
      next: (response: HttpResponse<ApiResponse<DownloadData>>) => {
        if (response.status !== 200 || !response.body?.data?.productions) {
          return;
        }

        const allData = response.body.data.productions.map((item: any) => ({
          ...item,
          downloadDocuments: 'print',
          processTypes: this.processTypes,
          process: '',
        }));
        const dataOriginal = this.tablaComponent.data;
        this.tablaComponent.data = allData;
        this.tablaComponent.exportarDatos(formato, headerInfo);
        this.tablaComponent.data = dataOriginal;
      },
      error: (error) => {
        console.error('Error al obtener las descargas para exportar:', error);
      },
    });
  }

  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  private getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  private getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }

  /**
   * Maneja la impresión de documentos según el tipo seleccionado
   * @param data Objeto con el item y el tipo de proceso a imprimir
   */
  onPrintDownload(data: { item: any; process: number }): void {

    if (!data.process) {
      console.error('Debe seleccionar un tipo de documento');
      return;
    }

    const processType = this.processTypes.find(
      (p) => p.process === Number(data.process)
    );

    if (!processType) {
      console.error('Tipo de documento no válido');
      return;
    }

    switch (Number(data.process)) {
      case 3:
        this.printInvoice(data.item);
        break;
      case 4:
        this.printParticularConditions(data.item);
        break;
      case 5:
        this.printEndorsements(data.item);
        break;
      case 6:
        this.printMarbetes(data.item);
        break;
      case 7:
        this.printAllDocuments(data.item);
        break;
      default:
        console.error('Tipo de documento no válido');
    }
  }

  /**
   * Imprime una factura específica
   * @param item Datos de la factura a imprimir
   */
  printInvoice(item: any) {
    const request = {
      process: 3,
      Policy: item.policyNumber.replaceAll('-', ''),
      DebtorCode: item.debtorCode,
      invoiceNumber: item.documentNumber,
      EffectiveDate: formatDate(item.workDate),
      cuser: this.authService.getUserId(),
      DocumentType: item.documentType,
    };

    this.utilitariosService.getPrintDocuments(request).subscribe({
      next: (response) => {


        this.gAnalytics.trackEvent('descarga_documentos_facturas', {
          page: '/descargas',
          action: 'Descarga de documentos de tipo factura',
          facturas_descargadas: 1
        })
        
        this.isFoundPrint = false;
        window.open(response.data.url, '_blank');
      },
      error: (error) => {
        this.noDocumentsAviableMessage = this.defaultNoDocumentsMessage;
        this.isFoundPrint = true;
        console.error('Error al obtener las órdenes:', error);
      },
    });
  }

  /**
   * Imprime las condiciones particulares de una póliza
   * @param item Datos de la póliza
   */
  printParticularConditions(item: any) {
    const request: DownloadDocumentsRequest = {
      process: 4,
      Policy: item.policyNumber.replaceAll('-', ''),
      DebtorCode: item.debtorCode,
      DocumentNumber: item.documentNumber,
      EffectiveDate: formatDate(item.efectiveDate),
      DocumentType: item.documentType,
      cuser: this.authService.getUserId(),
    };

    this.utilitariosService.getPrintDocuments(request).subscribe({
      next: (response) => {

        this.gAnalytics.trackEvent('descarga_documentos_condiciones_particulares', {
          page: '/descargas',
          action: 'Descarga de documentos de tipo condiciones particulares',
          condiciones_particulares_descargadas: 1
        })

        this.isFoundPrint = false;
        if (
          item.lob === 70 || item.lob < 20) {
          this.preventDownload = true;
        } else {
          window.open(response.data.url, '_blank');
        }
      },
      error: (error) => {
        this.noDocumentsAviableMessage = this.defaultNoDocumentsMessage;
        this.isFoundPrint = true;
        console.error('Error al obtener las órdenes:', error);
      },
    });
  }

  /**
   * Imprime los endosos de una póliza
   * @param item Datos del endoso
   */
  printEndorsements(item: any) {
    const request: DownloadEndorsementRequest = {
      PolicyNumber: item.policyNumber.replaceAll('-', ''),
      DocumentTypeCode: item.documentType,
      DocumentNumber: item.documentNumber,
    };
    this.utilitariosService.getEndorsement(request).subscribe({
      next: (response) => {

        this.gAnalytics.trackEvent('descarga_documentos_endosos', {
          page: '/descargas',
          action: 'Descarga de documentos de tipo endosos',
          endosos_descargados: 1
        })


        this.isFoundPrint = false;
        window.open(response.data.url, '_blank');
      },
      error: (error) => {
        this.noDocumentsAviableMessage = this.defaultNoDocumentsMessage;
        this.isFoundPrint = true;
        console.error('Error al obtener el documento:', error);
      },
    });
  }


  printMarbetes(item: any) {
    const formattedPolicy = item.policyNumber.replaceAll('-', '');
    const request = {
      process:5,
      policy: formattedPolicy,
      cuser: this.authService.getUserId(),
      debtorCode: item.debtorCode,
      DocumentType: item.documentType,
    }

    this.marbetesService.getAllCarnets(request).subscribe({
      next: (response: CarnetAllResponse) => {
        if (response.carnets.length) {
          const record = response.carnets[0]
          window.open(record.url, '_blank');
        } else {
          this.isFoundPrint = true;
          this.noDocumentsAviableMessage = 'No existen marbetes disponibles para la póliza seleccionada.';
        }
      },
      error: (error) => {
        console.error('Error al obtener los marbetes:', error);
      },
    })

  }

  /**
   * Descarga en secuencia facturas, condiciones particulares, endosos y marbetes.
   * Ejecuta cada endpoint cuando termina el anterior y crea enlaces temporales para descargar.
   * @param item Datos de la póliza
   */
  printAllDocuments(item: any) {
    this.isFoundPrint = false;
    this.loadingService.show();
    let hasDownloaded = false;

    const invoiceRequest = {
      process: 3,
      Policy: item.policyNumber.replaceAll('-', ''),
      DebtorCode: item.debtorCode,
      invoiceNumber: item.documentNumber,
      EffectiveDate: formatDate(item.workDate),
      cuser: this.authService.getUserId(),
      DocumentType: item.documentType,
    };

    const particularConditionsRequest: DownloadDocumentsRequest = {
      process: 4,
      Policy: item.policyNumber.replaceAll('-', ''),
      DebtorCode: item.debtorCode,
      DocumentNumber: item.documentNumber,
      EffectiveDate: formatDate(item.efectiveDate),
      DocumentType: item.documentType,
      cuser: this.authService.getUserId(),
    };

    const endorsementRequest: DownloadEndorsementRequest = {
      PolicyNumber: item.policyNumber.replaceAll('-', ''),
      DocumentTypeCode: item.documentType,
      DocumentNumber: item.documentNumber,
    };

    const marbetesRequest = {
      process: 5,
      policy: item.policyNumber.replaceAll('-', ''),
      cuser: this.authService.getUserId(),
      debtorCode: item.debtorCode,
      DocumentType: item.documentType,
    };

    const invoice$ = this.utilitariosService.getPrintDocuments(invoiceRequest).pipe(
      tap((response) => {
        if (response?.data?.url) {
          this.downloadDocumentByLink(response.data.url);
          hasDownloaded = true;
        }
      }),
      catchError((error) => {
        console.error('Error al obtener la factura:', error);
        return of(null);
      }),
      delay(3000)
    );

    const particularConditions$ = this.utilitariosService
      .getPrintDocuments(particularConditionsRequest)
      .pipe(
        tap((response) => {
          if (response?.data?.url && !(item.lob === 70 || item.lob < 20)) {
            this.downloadDocumentByLink(response.data.url);
            hasDownloaded = true;
          }
        }),
        catchError((error) => {
          console.error('Error al obtener las condiciones particulares:', error);
          return of(null);
        }),
        delay(3000)
      );

    const endorsement$ = this.utilitariosService
      .getEndorsement(endorsementRequest)
      .pipe(
        tap((response) => {
          if (response?.data?.url) {
            this.downloadDocumentByLink(response.data.url);
            hasDownloaded = true;
          }
        }),
        catchError((error) => {
          console.error('Error al obtener el endoso:', error);
          return of(null);
        }),
        delay(3000)
      );

    const marbetes$ = this.marbetesService.getAllCarnets(marbetesRequest).pipe(
      tap((response: CarnetAllResponse) => {
        if (response?.carnets?.length) {
          this.downloadDocumentByLink(response.carnets[0].url);
          hasDownloaded = true;
        }
      }),
      catchError((error) => {
        console.error('Error al obtener los marbetes:', error);
        return of(null);
      })
    );

    concat(invoice$, particularConditions$, endorsement$, marbetes$).subscribe({
      complete: () => {
        if (hasDownloaded) {
          this.gAnalytics.trackEvent('descarga_documentos_todos', {
            page: '/descargas',
            action: 'Descarga de todos los documentos',
            documentos_descargados: 1,
          });
        }
        this.loadingService.hide();
      },
      error: () => {
        this.loadingService.hide();
      },
    });
  }

  /**
   * Crea un enlace temporal y dispara la descarga del documento
   * @param url URL del documento a descargar
   */
  private downloadDocumentByLink(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Carga documentos generales
   * @param item Datos del documento a cargar
   */
  loadDocuments(item: DownloadDocumentsRequest) {
    this.utilitariosService.getPrintDocuments(item).subscribe({
      next: (response) => {
        this.isFoundPrint = false;
        window.open(response.data.url, '_blank');
      },
      error: (error) => {
        this.isFoundPrint = true;
        console.error('Error al obtener el documento:', error);
      },
    });
  }
}

/**
 * Formatea una fecha al formato yyyy-MM-dd enviado en la request
 * @param date Fecha a formatear
 * @returns Fecha formateada como objeto Date
 */
const formatDate = (date: string | Date | undefined): Date => {
  if (!date) {
    return new Date();
  }

  if (date instanceof Date) {
    return date;
  }

  const cleanDate = date.trim().replace(/\s+/g, '');
  const parts = cleanDate.split(/[-/.]/);

  if (parts.length === 3) {
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);

    const formattedDate = new Date(year, month, day);

    if (!isNaN(formattedDate.getTime())) {
      return formattedDate;
    }
  }

  return new Date();
};
